(() => {
  const cache = new Map();

  function getReviews(url) {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { signal: AbortSignal.timeout(12000) })
        .then((response) => {
          if (!response.ok) throw new Error(`Reviewbestand: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (data.status !== 'success' || !Array.isArray(data.ratings)) throw new Error('Reviews niet beschikbaar');
          return data.ratings.map(item => ({ nummer: item.id, naam: item.name, datum: item.date, cijfer: Number(item.rating) * 2, tekst: item.translations?.nl || item.comment || '' }));
        }));
    }
    return cache.get(url);
  }

  function selectReviews(reviews, order, limit) {
    const items = reviews.filter((item) => item && Number.isFinite(item.cijfer)).slice();
    if (order === 'random') {
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
    } else {
      items.sort((a, b) => {
        const rating = Number(b.cijfer || 0) - Number(a.cijfer || 0);
        const date = String(b.datum || '').localeCompare(String(a.datum || ''));
        if (order === 'best' && rating) return rating;
        return date || Number(a.nummer || 0) - Number(b.nummer || 0);
      });
    }
    return items.slice(0, Math.min(items.length, Math.max(1, limit || 12)));
  }

  function createCard(review, index, sectionId) {
    const card = document.createElement('article');
    card.className = 'wwk-reviews__card';

    const top = document.createElement('div');
    top.className = 'wwk-reviews__card-top';
    const score = document.createElement('span');
    score.className = 'wwk-reviews__score';
    score.textContent = review.cijfer == null ? '–' : String(review.cijfer);
    score.setAttribute('aria-label', `Beoordeling: ${score.textContent} uit 10`);

    const quote = document.createElement('span');
    quote.className = 'wwk-reviews__quote';
    quote.setAttribute('aria-hidden', 'true');
    quote.textContent = '“';
    top.append(quote, score);

    const body = document.createElement('div');
    body.className = 'wwk-reviews__body';
    body.id = `wwk-review-${sectionId}-${index}`;
    body.textContent = review.tekst || '';

    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'wwk-reviews__more';
    more.textContent = 'Lees meer';
    more.hidden = true;
    more.setAttribute('aria-controls', body.id);
    more.setAttribute('aria-expanded', 'false');
    more.addEventListener('click', () => {
      const expanded = body.classList.toggle('is-expanded');
      more.textContent = expanded ? 'Lees minder' : 'Lees meer';
      more.setAttribute('aria-expanded', String(expanded));
      card.closest('[data-wwk-reviews]')?.dispatchEvent(new Event('wwk:review-expanded'));
    });

    const meta = document.createElement('div');
    meta.className = 'wwk-reviews__meta';
    const name = document.createElement('p');
    name.className = 'wwk-reviews__name';
    name.textContent = review.naam || 'Klant';
    const date = document.createElement('p');
    date.className = 'wwk-reviews__date';
    if (review.datum && /^\d{4}-\d{2}-\d{2}$/.test(review.datum)) {
      const parsed = new Date(`${review.datum}T12:00:00`);
      date.textContent = Number.isNaN(parsed.getTime()) ? '' : new Intl.DateTimeFormat('nl-NL', {
        day: 'numeric', month: 'long', year: 'numeric'
      }).format(parsed);
    }
    if (date.textContent) meta.append(name, date);
    else meta.append(name);

    card.append(top, body, more, meta);
    return { card, body, more };
  }

  function initSection(section) {
    if (section.dataset.wwkInitialized === 'true') return;
    section.dataset.wwkInitialized = 'true';
    const track = section.querySelector('[data-review-track]');
    const controls = section.querySelector('[data-review-controls]');
    const progress = section.querySelector('[data-review-progress]');
    const previous = section.querySelector('[data-review-prev]');
    const next = section.querySelector('[data-review-next]');
    const error = section.querySelector('[data-review-error]');
    const carousel = section.querySelector('.wwk-reviews__carousel');
    if (!track || !controls || !progress || !previous || !next) return;

    const updateControls = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = Math.min(maximum, Math.max(0, track.scrollLeft));
      controls.hidden = maximum <= 2;
      previous.disabled = position <= 2;
      next.disabled = position >= maximum - 2;
      const visibleFraction = track.clientWidth / Math.max(track.scrollWidth, 1);
      const completion = maximum ? visibleFraction + (1 - visibleFraction) * position / maximum : 1;
      progress.style.width = `${Math.min(100, completion * 100)}%`;
      progress.parentElement.setAttribute('aria-valuenow', String(Math.round(completion * 100)));
    };
    const updateClamps = () => {
      track.querySelectorAll('.wwk-reviews__card').forEach((card) => {
        const body = card.querySelector('.wwk-reviews__body');
        const more = card.querySelector('.wwk-reviews__more');
        if (!body.classList.contains('is-expanded')) more.hidden = !body.textContent.trim() || body.scrollHeight <= body.clientHeight + 2;
      });
    };
    const step = (direction) => {
      const firstCard = track.querySelector('.wwk-reviews__card');
      if (!firstCard) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    previous.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', () => { updateControls(); updateClamps(); });
    section.addEventListener('wwk:review-expanded', updateControls);

    let autoplayDirection = 1;
    let pausedUntil = 0;
    carousel.addEventListener('pointerdown', () => { pausedUntil = Date.now() + 6000; });
    const autoplay = window.setInterval(() => {
      if (!section.isConnected) {
        window.clearInterval(autoplay);
        return;
      }
      if (document.hidden || carousel.matches(':hover') || carousel.contains(document.activeElement) ||
          Date.now() < pausedUntil || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const bounds = carousel.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;
      const maximum = track.scrollWidth - track.clientWidth;
      if (maximum <= 2) return;
      if (track.scrollLeft >= maximum - 2) autoplayDirection = -1;
      else if (track.scrollLeft <= 2) autoplayDirection = 1;
      step(autoplayDirection);
    }, 4200);

    getReviews(section.dataset.reviewsUrl)
      .then((reviews) => {
        if (!section.isConnected) return;
        const chosen = selectReviews(reviews, section.dataset.sort, parseInt(section.dataset.limit, 10));
        const cards = chosen.map((review, index) => createCard(review, index, section.id || 'section'));
        track.replaceChildren(...cards.map(({ card }) => card));
        requestAnimationFrame(() => { updateClamps(); updateControls(); });
        if (document.fonts?.ready) document.fonts.ready.then(updateClamps);
      })
      .catch((reason) => { console.error(reason); error.hidden = false; });
  }

  function init(root = document) {
    root.querySelectorAll('[data-wwk-reviews]').forEach(initSection);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
